import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Paper,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import axiosInstance from '@API/axios';
import BulkImportReviewDialog from '@Components/BulkImportReviewDialog';

/**
 * ExcelBulkImport component for bulk product import via Excel files.
 * Provides template download, file upload, and review dialog functionality.
 *
 * @param {Function} onProductsImported - Callback when products are successfully saved
 * @param {Array} existingProducts - Products already in the assessment
 * @param {Array} availableProducts - Products available in the dropdown
 * @param {string} downloadDraftUrl - Optional URL to download current draft as pre-filled Excel
 */
const ExcelBulkImport = ({ onProductsImported, existingProducts = [], availableProducts = [], downloadDraftUrl }) => {
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [downloadingDraft, setDownloadingDraft] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [pendingProducts, setPendingProducts] = useState([]);
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/product/template', {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Product_Import_Template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template:', err);
      setError('Failed to download template. Please try again.');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleDownloadDraft = async () => {
    if (!downloadDraftUrl) return;

    setDownloadingDraft(true);
    setError(null);
    try {
      const response = await axiosInstance.get(downloadDraftUrl, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Current_Draft_Products.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download draft:', err);
      setError('Failed to download current draft. Please try again.');
    } finally {
      setDownloadingDraft(false);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setError('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    setError(null);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post('/product/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;
      setImportResult(result);

      // Process valid products and open review dialog
      if (result.validationResults && result.validationResults.length > 0) {
        const validProducts = result.validationResults
          .filter(p => p.valid)
          .map(p => ({
            productId: p.existingProductId || null,
            productCucNumber: p.cucNumber,
            productName: p.productName,
            isAffected: p.isImpacted !== false,
            comment: p.impactComments || '',
            isExisting: p.exists,
            isTemporary: !p.exists,
          }));

        if (validProducts.length > 0) {
          // Open review dialog instead of directly adding products
          setPendingProducts(validProducts);
          setShowReviewDialog(true);
        }
      }
    } catch (err) {
      console.error('Failed to upload file:', err);
      setError(err.response?.data?.message || 'Failed to process Excel file. Please check the format and try again.');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleReviewDialogClose = () => {
    setShowReviewDialog(false);
    setPendingProducts([]);
  };

  const handleReviewDialogApply = (savedProducts) => {
    setShowReviewDialog(false);
    setPendingProducts([]);
    
    if (savedProducts.length > 0 && onProductsImported) {
      onProductsImported(savedProducts);
    }
  };

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Bulk Import Products
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={downloadingTemplate ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
            >
              {downloadingTemplate ? 'Downloading...' : 'Download Template'}
            </Button>
            {downloadDraftUrl && (
              <Button
                variant="outlined"
                size="small"
                startIcon={downloadingDraft ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleDownloadDraft}
                disabled={downloadingDraft}
                color="primary"
              >
                {downloadingDraft ? 'Downloading...' : 'Download Impact Analysis'}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
              onClick={handleFileSelect}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Excel'}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {importResult && !showReviewDialog && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${importResult.existingProducts} existing`}
                size="small"
                color="success"
                variant="outlined"
              />
              <Chip
                icon={<WarningIcon />}
                label={`${importResult.newProducts} new`}
                size="small"
                color="warning"
                variant="outlined"
              />
              {importResult.invalidProducts > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${importResult.invalidProducts} invalid`}
                  size="small"
                  color="error"
                  variant="outlined"
                />
              )}
              {importResult.duplicatesRemoved > 0 && (
                <Chip
                  label={`${importResult.duplicatesRemoved} duplicates removed`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            {importResult.invalidProducts > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {importResult.invalidProducts} product(s) could not be imported. Check that all required fields are filled.
              </Alert>
            )}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Download the template, fill in product details, then upload to bulk import.
        </Typography>
      </Paper>

      {/* Review Dialog */}
      <BulkImportReviewDialog
        open={showReviewDialog}
        onClose={handleReviewDialogClose}
        importedProducts={pendingProducts}
        onApply={handleReviewDialogApply}
      />
    </>
  );
};

export default ExcelBulkImport;
